import React, { useMemo } from "react";
import { useSelector } from "react-redux";

import cn from "classnames";
import { useIntl } from "react-intl";

import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";

import ActivityTypeIcon from "@app/component/activity/ActivityTypeIcon";
import { getCoachingTaskActivityType } from "@app/store/activity";

import styles from "./useActivityTypeOptions.module.scss";

const no = () => false;

export interface ActivityTypeOption {
  disabled: boolean;
  label: React.ReactNode;
  text: string;
  value: ActivityType["id"];
}

const useActivityTypeOptions = (
  activityTypes: ActivityType[],
  value?: ActivityType["id"] | Array<ActivityType["id"]>,
  isActivityTypeDisabled: (type: ActivityType) => boolean | undefined = no
): ActivityTypeOption[] => {
  const intl = useIntl();
  const coachingTaskTypeId = useSelector(getCoachingTaskActivityType)?.id;
  return useMemo(() => {
    const values = Array.isArray(value) ? value : [value];
    return activityTypes
      .filter(
        ({ id, active, deletedAt }) =>
          ((!deletedAt && active) || values?.includes(id)) &&
          (id !== coachingTaskTypeId || values?.includes(id))
      )
      .sort((a, b) => {
        const aDisabled = isActivityTypeDisabled(a);
        const bDisabled = isActivityTypeDisabled(b);
        return aDisabled === bDisabled ? 0 : aDisabled ? 1 : -1;
      })
      .map((activityType) => {
        const isDeleted = !!activityType.deletedAt;
        const isInactive = !activityType.active && !isDeleted;
        const displayName = intl.formatMessage(
          {
            id: "activityOptions.displayName",
            defaultMessage:
              "{name}{isDeleted, select, true { (Deleted)} other {}}{isDisabled, select, true { (Disabled)} other {}}",
            description: "Activity type name with status info about its deleted or disabled state",
          },
          {
            isDeleted,
            isDisabled: isInactive,
            name: activityType.name,
          }
        );
        return {
          disabled: isActivityTypeDisabled(activityType) || !activityType.active || isDeleted,
          label: (
            <>
              <ActivityTypeIcon
                activityType={activityType}
                className={cn({
                  [styles.disabledIcon]:
                    !activityType.active || isDeleted || isActivityTypeDisabled(activityType),
                })}
                size="sm"
              />
              <span className={styles.textMargin}>{displayName}</span>
            </>
          ),
          text: displayName,
          value: activityType.id,
        };
      });
  }, [activityTypes, coachingTaskTypeId, intl, isActivityTypeDisabled, value]);
};

export default useActivityTypeOptions;
